// IEEE single precision helpers. The original engine stores everything in C
// `float`s; to reproduce its results bit for bit (SSE2 builds, no FMA) every
// float operation is rounded with Math.fround, following the C expression
// types exactly: float op float -> float, while anything involving a double
// literal (0.5, 1.0, 127.0, sqrt(), sin()...) is evaluated in double and only
// rounded when stored back into a float.

import type { Vec3 } from './math';

export const f = Math.fround;

/** q_shared.h M_PI is a float literal on the MSVC builds. */
export const M_PI_F = f(Math.PI);

/** DotProduct macro: ((a0*b0 + a1*b1) + a2*b2) in float. */
export const fdot = (a: ArrayLike<number>, b: ArrayLike<number>): number => f(f(f(a[0] * b[0]) + f(a[1] * b[1])) + f(a[2] * b[2]));

/** VectorLength: float sum of squares, double sqrt, stored as float. */
export const flength = (v: ArrayLike<number>): number => f(Math.sqrt(fdot(v, v)));

/** VectorNormalize (in place), returns the float length. */
export function fnormalize(v: Vec3): number {
  const length = f(Math.sqrt(fdot(v, v)));
  if (length) {
    const ilength = f(1 / length);
    v[0] = f(v[0] * ilength);
    v[1] = f(v[1] * ilength);
    v[2] = f(v[2] * ilength);
  }
  return length;
}

/** VectorNormalize2 */
export function fnormalize2(v: ArrayLike<number>, out: Vec3): number {
  const length = f(Math.sqrt(fdot(v, v)));
  if (length) {
    const ilength = f(1 / length);
    out[0] = f(v[0] * ilength);
    out[1] = f(v[1] * ilength);
    out[2] = f(v[2] * ilength);
  } else {
    out[0] = out[1] = out[2] = 0;
  }
  return length;
}

/** VectorMA: out = v + b * s (all floats) */
export function fma3(out: Vec3, v: ArrayLike<number>, s: number, b: ArrayLike<number>): Vec3 {
  out[0] = f(v[0] + f(b[0] * s));
  out[1] = f(v[1] + f(b[1] * s));
  out[2] = f(v[2] + f(b[2] * s));
  return out;
}

/** VectorScale */
export function fscale(out: Vec3, v: ArrayLike<number>, s: number): Vec3 {
  out[0] = f(v[0] * s);
  out[1] = f(v[1] * s);
  out[2] = f(v[2] * s);
  return out;
}

/** CrossProduct */
export function fcross(out: Vec3, a: ArrayLike<number>, b: ArrayLike<number>): Vec3 {
  const x = f(f(a[1] * b[2]) - f(a[2] * b[1]));
  const y = f(f(a[2] * b[0]) - f(a[0] * b[2]));
  const z = f(f(a[0] * b[1]) - f(a[1] * b[0]));
  out[0] = x;
  out[1] = y;
  out[2] = z;
  return out;
}

const DEG_F = f(f(M_PI_F * 2) / 360);

/** AngleVectors from q_math.c (float angles, double sin/cos stored as float). */
export function fangleVectors(angles: ArrayLike<number>, forward: Vec3 | null, right: Vec3 | null, up: Vec3 | null): void {
  let angle = f(angles[1] * DEG_F);
  const sy = f(Math.sin(angle));
  const cy = f(Math.cos(angle));
  angle = f(angles[0] * DEG_F);
  const sp = f(Math.sin(angle));
  const cp = f(Math.cos(angle));
  angle = f(angles[2] * DEG_F);
  const sr = f(Math.sin(angle));
  const cr = f(Math.cos(angle));

  if (forward) {
    forward[0] = f(cp * cy);
    forward[1] = f(cp * sy);
    forward[2] = -sp;
  }
  if (right) {
    // ( -1 * sr * sp * cy + - 1 * cr * -sy ) evaluated left to right in float
    right[0] = f(f(f(f(-1 * sr) * sp) * cy) + f(f(-1 * cr) * -sy));
    right[1] = f(f(f(f(-1 * sr) * sp) * sy) + f(f(-1 * cr) * cy));
    right[2] = f(f(-1 * sr) * cp);
  }
  if (up) {
    up[0] = f(f(f(cr * sp) * cy) + f(-sr * -sy));
    up[1] = f(f(f(cr * sp) * sy) + f(-sr * cy));
    up[2] = f(cr * cp);
  }
}

/** vectoangles from q_math.c */
export function fvectoangles(v: ArrayLike<number>, out: Vec3): Vec3 {
  let yaw: number;
  let pitch: number;
  if (v[1] === 0 && v[0] === 0) {
    yaw = 0;
    pitch = v[2] > 0 ? 90 : 270;
  } else {
    if (v[0]) yaw = f((Math.atan2(v[1], v[0]) * 180) / M_PI_F);
    else if (v[1] > 0) yaw = 90;
    else yaw = 270;
    if (yaw < 0) yaw = f(yaw + 360);
    const forward = f(Math.sqrt(f(f(v[0] * v[0]) + f(v[1] * v[1]))));
    pitch = f((Math.atan2(v[2], forward) * 180) / M_PI_F);
    if (pitch < 0) pitch = f(pitch + 360);
  }
  out[0] = -pitch;
  out[1] = yaw;
  out[2] = 0;
  return out;
}

/** rintf: round half to even (SnapVector). */
export function rintf(x: number): number {
  const r = Math.round(x);
  return Math.abs(x % 1) === 0.5 && r % 2 !== 0 ? r - 1 : r;
}
